import { useState, useEffect, useRef, useCallback } from "react";
import { useLocation } from "@/lib/router-compat";
import { ArrowUp, MessageCircle, X } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

const HUBSPOT_PORTAL_ID = "7030766";
const HUBSPOT_DEFAULT_FORM_ID = "68016d36-003a-48fd-9eb8-8f9cb7128783";

const ScrollToTop = () => {
  const [visible, setVisible] = useState(false);
  const [timeReady, setTimeReady] = useState(false);
  const [formOpen, setFormOpen] = useState(false);
  const formContainerRef = useRef<HTMLDivElement>(null);
  const scriptLoadedRef = useRef(false);
  const location = useLocation();
  const [hasBrowseFab, setHasBrowseFab] = useState(false);

  const [activeFormId, setActiveFormId] = useState(HUBSPOT_DEFAULT_FORM_ID);
  const [specialistLabel, setSpecialistLabel] = useState("a specialist");
  const prevFormIdRef = useRef(HUBSPOT_DEFAULT_FORM_ID);

  // Detect if the Browse FAB is present on this page
  useEffect(() => {
    const timer = setTimeout(() => {
      setHasBrowseFab(!!document.getElementById("commercial-browse-fab"));
    }, 100);
    return () => clearTimeout(timer);
  }, [location.pathname]);

  useEffect(() => {
    let mounted = true;

    if (!location.pathname.startsWith("/en/services")) {
      setActiveFormId(HUBSPOT_DEFAULT_FORM_ID);
      setSpecialistLabel("a specialist");
      return;
    }

    import("@/data/servicesData")
      .then(({ getServiceByPath }) => {
        if (!mounted) return;
        const service = getServiceByPath(location.pathname);
        setActiveFormId(service?.hubspotFormId || HUBSPOT_DEFAULT_FORM_ID);
        setSpecialistLabel(service?.specialistLabel || "a specialist");
      })
      .catch(() => {
        if (mounted) {
          setActiveFormId(HUBSPOT_DEFAULT_FORM_ID);
          setSpecialistLabel("a specialist");
        }
      });

    return () => {
      mounted = false;
    };
  }, [location.pathname]);

  // Time delay — only allow visibility after 15 seconds on the page
  useEffect(() => {
    setTimeReady(false);
    const timer = setTimeout(() => setTimeReady(true), 15000);
    return () => clearTimeout(timer);
  }, [location.pathname]);

  useEffect(() => {
    const onScroll = () => setVisible(window.scrollY > 400 && timeReady);
    window.addEventListener("scroll", onScroll);
    // Re-evaluate when timeReady changes
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, [timeReady]);

  const loadHubspotForm = useCallback(() => {
    if (!formContainerRef.current) return;

    const createForm = () => {
      if (formContainerRef.current && (window as any).hbspt) {
        formContainerRef.current.innerHTML = "";
        (window as any).hbspt.forms.create({
          portalId: HUBSPOT_PORTAL_ID,
          formId: activeFormId,
          region: "na1",
          target: "#hubspot-popup-form",
        });
      }
    };

    if ((window as any).hbspt && scriptLoadedRef.current) {
      createForm();
      return;
    }

    const existingScript = document.querySelector(
      'script[src*="js.hsforms.net"]'
    );

    if (existingScript) {
      if ((window as any).hbspt) {
        scriptLoadedRef.current = true;
        createForm();
      } else {
        existingScript.addEventListener("load", () => {
          scriptLoadedRef.current = true;
          createForm();
        });
      }
      return;
    }

    const script = document.createElement("script");
    script.src = "//js.hsforms.net/forms/embed/v2.js";
    script.charset = "utf-8";
    script.async = true;
    script.onload = () => {
      scriptLoadedRef.current = true;
      createForm();
    };
    document.body.appendChild(script);
  }, [activeFormId]);

  // Reload form when formId changes (navigating between service pages)
  useEffect(() => {
    if (activeFormId !== prevFormIdRef.current) {
      prevFormIdRef.current = activeFormId;
      if (formOpen) loadHubspotForm();
    }
  }, [activeFormId, formOpen, loadHubspotForm]);

  useEffect(() => {
    if (formOpen) {
      loadHubspotForm();
    }
  }, [formOpen, loadHubspotForm]);

  return (
    <>
      <AnimatePresence>
        {visible && (
          <>
            {/* Scroll to top */}
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.8 }}
              className="fixed bottom-6 right-6 z-50"
            >
              <button
                onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
                className="flex h-11 w-11 items-center justify-center rounded-full bg-primary text-primary-foreground shadow-lg hover:bg-primary/90 hover:shadow-xl hover:scale-105 transition-all duration-300"
                aria-label="Scroll to top"
              >
                <ArrowUp size={20} />
              </button>
            </motion.div>

            {/* Talk to specialist — above browse popup */}
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.8 }}
              className={`fixed right-6 z-50 ${hasBrowseFab ? "bottom-[8.5rem]" : "bottom-[4.75rem]"}`}
            >
              <button
                onClick={() => setFormOpen(true)}
                className="group flex h-11 w-11 items-center justify-center rounded-full bg-qbd-blue text-white shadow-lg hover:shadow-xl hover:scale-105 transition-all duration-300 relative"
                aria-label={`Talk to ${specialistLabel}`}
              >
                <motion.span
                  className="absolute inset-0 rounded-full bg-qbd-blue"
                  initial={{ scale: 1, opacity: 0.5 }}
                  animate={{ scale: [1, 1.5, 1], opacity: [0.5, 0, 0.5] }}
                  transition={{ duration: 2, repeat: 2, ease: "easeOut" }}
                />
                <MessageCircle size={20} className="relative z-10" />
                <span className="absolute right-full mr-3 whitespace-nowrap bg-foreground/90 text-background text-xs font-medium px-3 py-1.5 rounded-lg opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity duration-200">
                  Talk to {specialistLabel}
                </span>
              </button>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* HubSpot Form Pop-up */}
      <AnimatePresence>
        {formOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4"
            onClick={(e) => {
              if (e.target === e.currentTarget) setFormOpen(false);
            }}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.9, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 20 }}
              transition={{ type: "spring", damping: 25, stiffness: 300 }}
              className="relative w-full max-w-lg max-h-[90vh] overflow-y-auto bg-background rounded-2xl border border-border shadow-2xl"
            >
              <div className="sticky top-0 z-10 flex items-center justify-between bg-background/95 backdrop-blur-sm px-6 pt-6 pb-4 border-b border-border/50">
                <div>
                  <h2 className="text-lg font-bold text-foreground">Talk to {specialistLabel}</h2>
                  <p className="text-sm text-muted-foreground">Fill in the form and we'll get back to you shortly.</p>
                </div>
                <button
                  onClick={() => setFormOpen(false)}
                  className="flex h-8 w-8 items-center justify-center rounded-full hover:bg-muted transition-colors"
                  aria-label="Close"
                >
                  <X size={18} />
                </button>
              </div>
              <div className="p-6">
                <div
                  id="hubspot-popup-form"
                  ref={formContainerRef}
                  className="min-h-[300px] [&_label]:text-sm [&_label]:font-medium [&_label]:text-foreground/80 [&_label]:font-[Open_Sans,sans-serif] [&_input]:rounded-lg [&_input]:border-border [&_input]:font-[Open_Sans,sans-serif] [&_input]:text-sm [&_select]:rounded-lg [&_select]:border-border [&_select]:font-[Open_Sans,sans-serif] [&_select]:text-sm [&_textarea]:rounded-lg [&_textarea]:border-border [&_textarea]:font-[Open_Sans,sans-serif] [&_textarea]:text-sm [&_.hs-submit]:mt-4 [&_.hs-button]:!bg-accent [&_.hs-button]:!border-accent [&_.hs-button]:rounded-full [&_.hs-button]:font-semibold [&_.hs-button]:font-[Open_Sans,sans-serif] [&_.hs-button]:px-8 [&_.hs-button]:py-3 [&_.hs-error-msgs]:text-xs [&_.hs-error-msgs]:text-destructive [&_.hs-form-field]:mb-4"
                />
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default ScrollToTop;
